import { useEffect, useRef, useState } from "react";
import { api } from "./api";
import { readDashboardStorage } from "./dashboardGadgets";
import { toast } from "sonner";

let gadgetWrites = Promise.resolve();
export default function useDashboardGadgets({ role, userId }) {
    const storageKey = `shadowGroveDashboardGadgets_${role === "child" ? "child_" : ""}${userId || "local"}`;
    const url = role === "child" ? "/auth/child/dashboard/gadgets" : "/dashboard/gadgets";
    const initial = () => { const saved = readDashboardStorage(storageKey, null); return Array.isArray(saved) ? saved : role === "child" ? [{ id: "daily-classrooms", type: "assignments", title: "Today's classroom", items: [], color: "#c084fc" }] : []; };
    const [gadgets, setGadgets] = useState(initial), [progress, setProgress] = useState([]), [busy, setBusy] = useState(false);
    const mounted = useRef(true);
    const ref = useRef(gadgets), revision = useRef(0), timer = useRef(null), pending = useRef(false);
    const apply = (data) => { if (!mounted.current || !Array.isArray(data?.gadgets)) return; ref.current = data.gadgets; setGadgets(data.gadgets); setProgress(data.progress || []); localStorage.setItem(storageKey, JSON.stringify(data.gadgets)); };
    const flush = () => {
        if (!userId || !pending.current) return gadgetWrites;
        pending.current = false;
        const version = revision.current, snapshot = ref.current;
        gadgetWrites = gadgetWrites.then(async () => {
            try { const response = await api.put(url, { gadgets: snapshot, owner_id: role === "child" ? userId : "parent", ...(role === "parent" ? { user_id: userId } : {}) }); if (version === revision.current) apply(response?.data); }
            catch (error) { pending.current = true; toast.error(error?.response?.data?.detail || "Dashboard saved here. Shared gadget sync will retry."); }
        });
        return gadgetWrites;
    };
    const persist = (next) => {
        try { localStorage.setItem(storageKey, JSON.stringify(next)); } catch { toast.error("Dashboard could not be saved. Remove old items and try again."); return false; }
        revision.current += 1; ref.current = next; setGadgets(next); pending.current = true;
        clearTimeout(timer.current); timer.current = setTimeout(flush, 400); return true;
    };
    useEffect(() => {
        if (!userId) return;
        mounted.current = true;
        let cancelled = false, inFlight = false;
        const refresh = async () => {
            if (inFlight) return; inFlight = true;
            await flush(); await gadgetWrites;
            const version = revision.current;
            try {
                const response = await api.get(url);
                if (cancelled || version !== revision.current || pending.current || !Array.isArray(response?.data?.gadgets)) return;
                if (!response.data.initialized) { const own = ref.current; persist([...own, ...response.data.gadgets.filter((g) => !own.some((item) => item.id === g.id))]); }
                else apply(response.data);
            } catch { /* Saved local lists remain usable. */ } finally { inFlight = false; }
        };
        const storage = (event) => { if (event.key === storageKey) { const next = readDashboardStorage(storageKey, []); ref.current = next; setGadgets(next); } };
        void refresh(); const interval = window.setInterval(refresh, 15000);
        window.addEventListener("focus", refresh); window.addEventListener("storage", storage);
        return () => { cancelled = true; mounted.current = false; clearTimeout(timer.current); void flush(); clearInterval(interval); window.removeEventListener("focus", refresh); window.removeEventListener("storage", storage); };
        // Scope changes remount DashboardGadgets; callbacks read its latest ref.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [url, storageKey, userId]);
    const complete = async (gadgetId, taskId, childId, payload) => {
        setBusy(true);
        try {
            await flush(); await gadgetWrites;
            const version = revision.current;
            const path = role === "child" ? `/auth/child/dashboard/todos/${encodeURIComponent(gadgetId)}/tasks/${encodeURIComponent(taskId)}` : `/dashboard/todos/${encodeURIComponent(gadgetId)}/children/${encodeURIComponent(childId)}/tasks/${encodeURIComponent(taskId)}`;
            const response = await api.patch(path, { ...payload, child_id: childId });
            if (version === revision.current) apply(response.data);
            else if (mounted.current) {
                setProgress(response.data.progress || []);
                const remote = new Map((response.data.gadgets || []).map((g) => [g.id, g]));
                const next = ref.current.map((g) => g.assigned && remote.has(g.id) ? { ...g, items: remote.get(g.id).items } : g);
                ref.current = next; setGadgets(next); localStorage.setItem(storageKey, JSON.stringify(next));
            } window.dispatchEvent(new CustomEvent("shadowGroveMeritsUpdated")); return true;
        } catch (error) { toast.error(error?.response?.data?.detail || "Completion could not sync. Please try again."); return false; } finally { if (mounted.current) setBusy(false); }
    };
    return { gadgets, gadgetsRef: ref, progress, persist, complete, busy };
}
